import { AppShell, type AppShellProps } from '@easydoctor/ui';
import { ROLE_LABELS } from '@easyhelp/shared';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { IconLifebuoy } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useLogout, useSession } from '../api/auth';
import { useOpenTicketCount } from '../api/tickets';
import { useUnassociatedMailCount } from '../api/unassociated-mail';
import { RouterLink } from '../components/RouterLink';
import { footerNavFor, isWidePage, navFor } from './nav';

/** Nome della suite delle console interne (DEC-001). */
export const SUITE_NAME = 'EasyHelp';

/** Nome della console nel brand della sidebar, come nel prototipo. */
const CONSOLE_NAME = 'Console Assistenza';

/** Il quadrato del brand: salvagente bianco su `primary.main`, raggio 8 (prototipo); 28px da compressa (CMP-001). */
function BrandMark({ size = 32 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 2,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconLifebuoy size={18} aria-hidden />
    </Box>
  );
}

const BRAND: AppShellProps['brand'] = {
  full: (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2.25, minWidth: 0 }}>
      <BrandMark />
      <Stack sx={{ minWidth: 0 }}>
        {/* `span`: con `subtitle2` MUI renderebbe un `h6`, un'intestazione prima dell'`h1` della pagina. */}
        <Typography variant="subtitle2" component="span" noWrap sx={{ fontWeight: 600, color: 'text.primary' }}>
          {CONSOLE_NAME}
        </Typography>
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
          Easy Health · interno
        </Typography>
      </Stack>
    </Stack>
  ),
  compact: <BrandMark size={28} />,
  href: '/',
  label: CONSOLE_NAME,
};

/** Chiave di `localStorage` della sidebar compressa (come `miniDrawer` nella configurazione di EasyCare). */
export const SIDEBAR_COLLAPSED_KEY = 'easyhelp.sidebar.collapsed';

/** La sidebar era compressa all'ultima visita? Senza storage (navigazione privata, blocchi) è aperta. */
function readSidebarCollapsed(): boolean {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) ?? 'false') === true;
  } catch {
    return false;
  }
}

/** Ricorda la scelta per la prossima visita; senza storage la scelta vale solo fino al ricaricamento. */
function writeSidebarCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(collapsed));
  } catch {
    // Storage non disponibile: la sidebar funziona lo stesso, senza ricordare.
  }
}

/**
 * Shell della suite in EasyHelp (DEC-001, CMP-001): adattatore sottile che costruisce le props di
 * `AppShell` di `@easydoctor/ui` da router, sessione e contatori. Voci per ruolo: Coda (badge dei ticket aperti), Posta
 * non associata (email in attesa), Statistiche (amministratore), Guida e, in fondo, Impostazioni
 * (amministratore). Sta dentro `RequireSession`: la sessione in cache c'è sempre.
 *
 * La sidebar compressa resta compressa anche dopo un ricaricamento: la scelta sta in `localStorage`
 * (`SIDEBAR_COLLAPSED_KEY`), letta e scritta senza errori anche quando lo storage non c'è.
 *
 * Il breadcrumb parte dalla `homeCrumb` predefinita, «Home» verso la Coda come in EasyCare: sulla
 * Coda e sul dettaglio è solo «Coda» (niente «Home › Coda» che porta allo stesso indirizzo), sulle
 * altre sezioni «Home › Guida».
 */
export function SuiteShell() {
  const { data: session } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Finché i contatori non arrivano, o se la lettura fallisce, le voci restano senza badge.
  const { data: openTicketCount } = useOpenTicketCount();
  const { data: unassociatedMail } = useUnassociatedMailCount();
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const member = session?.member;
  const role = member?.role;

  const mailCount = unassociatedMail?.count;
  const nav = useMemo(
    () => navFor(role, { openTickets: openTicketCount, unassociatedMail: mailCount }),
    [role, openTicketCount, mailCount]
  );
  const footerNav = useMemo(() => footerNavFor(role), [role]);

  const changeCollapsed = (next: boolean) => {
    setCollapsed(next);
    writeSidebarCollapsed(next);
  };

  const exit = () => {
    logout.mutate(undefined, { onSettled: () => void navigate('/accesso', { replace: true }) });
  };

  return (
    <AppShell
      brand={BRAND}
      nav={nav}
      footerNav={footerNav}
      pathname={pathname}
      LinkComponent={RouterLink}
      user={{
        name: member?.name ?? '',
        secondary: member?.email,
        role: role ? ROLE_LABELS[role] : undefined,
      }}
      onLogout={exit}
      logoutPending={logout.isPending}
      collapsed={collapsed}
      onCollapsedChange={changeCollapsed}
      maxWidth={isWidePage(pathname) ? 'xl' : 'lg'}
    >
      <Outlet />
    </AppShell>
  );
}
